import { useTranslation } from 'react-i18next';
import { Address } from '@hero/schema';
import { AlertCircleIcon, CheckIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { Form } from '../../../ui/Form';
import { CustomFormField } from '../../../app/CustomFormField';
import { FormFieldTypes } from '@hero/web-types';
import { Button } from '../../../ui/Button';
import { Spinner } from '../../../app/Spinner';
import { ClientSelector, ClientSelectorProps } from '../../Client/Selector/ClientSelector';
import { ClientLinkingBanner } from '../../Client/LinkingBanner';
import { ContactSelector, ContactSelectorProps } from '../../Contact/Selector/ContactSelector';
import {
  useSavedLocationForm,
  SavedLocationFormValues,
  UseSavedLocationFormOptions,
} from '@hero/web-hooks';
import { LocationTypeSelector } from './SavedLocationTypeSelector';
import { SavedLocationMap } from '../Map/SavedLocationMap';

/** Submit payload type for LocationForm onSubmit */
export type LocationFormSubmitData = SavedLocationFormValues;
/** Default values type when editing / opening modal */
export type SavedLocationFormDefaultValues = UseSavedLocationFormOptions['defaultValues'];

export interface LocationFormProps {
  isLoading?: boolean;
  /** Hide submit button (e.g. when used inside ModalForm with external save) */
  hideSubmitButton?: boolean;
  /** optional props for ClientSelector */
  clientProps?: ClientSelectorProps;
  /** optional props for ContactSelector */
  contactProps?: ContactSelectorProps;
  /** the form hook initialized by parent component */
  formHook: ReturnType<typeof useSavedLocationForm>;
  /** When editing a saved location, pass its address so the map shows address in search bar and card instead of raw coordinates */
  initialAddress?: Address;
}

export function LocationForm({
  isLoading = false,
  hideSubmitButton = false,
  clientProps,
  contactProps,
  formHook,
  initialAddress,
}: LocationFormProps) {
  const { t } = useTranslation(['m-location', 'c-main']);
  const {
    values,
    form,
    handleClientsSelected,
    handleContactSave,
    handleContactsSelect,
    handleClientSave,
    isContactSelectorDisabled,
    onValidSubmit,
    isLocationTypeLocked,
    isClientLocked,
    resolveLocationUrl,
    isResolvingLocationUrl,
    locationUrlError,
  } = formHook;

  return (
    <>
      <Form {...form}>
        <div className="space-y-4">
          {isClientLocked && values.clientId ? (
            <ClientLinkingBanner
              clientId={values.clientId}
              caption={t('modal.client_locked_banner')}
            />
          ) : null}

          {!isLocationTypeLocked && (
            <LocationTypeSelector
              value={values.locationType}
              onChange={value => {
                form.setValue('locationType', value);
                form.setValue('clientId', undefined);
                form.setValue('contacts', []);
              }}
            />
          )}

          {values.locationType === 'client' && !isClientLocked && (
            <div>
              <ClientSelector
                {...clientProps}
                selectedClients={values.clientId}
                onClientsSelected={handleClientsSelected}
                onClientSave={handleClientSave}
                label={t('form.client.title')}
                required
              />
            </div>
          )}

          {/*
           * Place name + contact share a row when there's room (the
           * order-publish drawer renders the form at ~880px). On
           * narrow surfaces the grid collapses to single-column so
           * neither field gets clipped — same fallback the form had
           * before the drawer-width bump.
           */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CustomFormField
              control={form.control}
              name="name"
              filedType={FormFieldTypes.INPUT}
              placeholder={t('form.place_name.placeholder')}
              label={t('form.place_name.title')}
            />

            <ContactSelector
              {...contactProps}
              customerId={values.clientId}
              selectedContacts={values.contacts}
              onContactsChange={handleContactsSelect}
              onContactSave={handleContactSave}
              disabled={isContactSelectorDisabled}
              label={t('form.contact.title')}
              required
            />
          </div>

          {/*
           * Pasting a share link is the escape hatch for "I can't drag
           * the pin to the right spot". Resolving on blur moves the map
           * and marks the pin exact, so the link is an input to the
           * location rather than a note filed next to it.
           */}
          <div>
            <CustomFormField
              control={form.control}
              name="locationUrl"
              filedType={FormFieldTypes.INPUT}
              placeholder={t('form.location_url.placeholder')}
              label={t('form.location_url.title')}
              onBlur={resolveLocationUrl}
            />
            {isResolvingLocationUrl && (
              <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                <Spinner className="size-3" />
                {t('form.location_url.resolving')}
              </p>
            )}
            {!isResolvingLocationUrl && locationUrlError && (
              <p className="mt-1 flex items-center gap-1.5 text-xs text-amber-700">
                <AlertCircleIcon className="size-3.5 shrink-0" />
                {locationUrlError === 'invalid'
                  ? t('form.location_url.error_invalid')
                  : t('form.location_url.error_unresolvable')}
              </p>
            )}
          </div>

          {/*
           * Accuracy decision lives ABOVE the map so it frames the
           * map interaction: the user reads the question, drops or
           * drags a pin, then commits to "yes, that's exact" or "no,
           * I'll refine later". Mirrors the flag on order
           * destinations + the persisted SavedLocation field. A
           * segmented two-button control reads more decisively than
           * a single checkbox tucked at the bottom.
           */}
          <AccuracyToggle
            value={form.watch('isAccurate') ?? true}
            onChange={next => form.setValue('isAccurate', next)}
          />

          <SavedLocationMap
            coordinates={form.watch('location')}
            initialAddress={initialAddress}
            enterEditModeInitially={!initialAddress}
            onLocationChange={locationInfo => form.setValue('location', locationInfo.cords)}
            onLocationEditStart={formHook.onLocationEditStart}
            onLocationEditEnd={formHook.onLocationEditEnd}
            showSearch={true}
            showAddressDisplay={true}
          />

          {!hideSubmitButton && (
            <Button
              type="button"
              className="w-full"
              disabled={isLoading}
              onClick={() => form.handleSubmit(onValidSubmit)()}>
              {isLoading ? (
                <>
                  <Spinner className="me-2" />
                  <span>{t('c-main:async_operations.saving')}</span>
                </>
              ) : (
                <span>{t('c-main:crud_buttons.save')}</span>
              )}
            </Button>
          )}
        </div>
      </Form>
    </>
  );
}

interface AccuracyToggleProps {
  value: boolean;
  onChange: (next: boolean) => void;
}

/**
 * Two-button segmented control that frames the map interaction with
 * the accuracy decision: "is this pin exact, or are you approximating
 * for now?". Lives above the map (not below) so the user sees the
 * question before they start dragging the pin and answers it
 * decisively, instead of stumbling into a checkbox at the bottom of
 * the form. Renders the SAR-style emerald/amber pair we use for
 * positive/warning states elsewhere.
 */
function AccuracyToggle({ value, onChange }: AccuracyToggleProps) {
  const { t } = useTranslation('m-location');
  const buttonClass = (active: boolean, tone: 'emerald' | 'amber') =>
    cn(
      'flex flex-1 items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
      active
        ? tone === 'emerald'
          ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
          : 'border-amber-300 bg-amber-50 text-amber-800'
        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50',
    );

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50/60 p-3">
      <div className="text-sm font-semibold text-gray-900">
        {t('form.accuracy.question', { defaultValue: 'Is this location accurate?' })}
      </div>
      <div className="mb-3 text-xs text-gray-500">
        {t('form.accuracy.help', {
          defaultValue:
            "Drag the pin or use search to drop the exact spot. Choose \u201CNo\u201D if you can\u2019t pin it precisely yet \u2014 dispatch will be warned the driver may need to call on arrival.",
        })}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange(true)}
          className={buttonClass(value === true, 'emerald')}
          aria-pressed={value === true}>
          <CheckIcon className="size-4" />
          {t('form.accuracy.yes', { defaultValue: 'Yes, exact pin' })}
        </button>
        <button
          type="button"
          onClick={() => onChange(false)}
          className={buttonClass(value === false, 'amber')}
          aria-pressed={value === false}>
          <AlertCircleIcon className="size-4" />
          {t('form.accuracy.no', { defaultValue: "No \u2014 I\u2019ll refine later" })}
        </button>
      </div>
    </div>
  );
}
